import React, { useState, useEffect, useMemo } from 'react';
import {
  useNoteSyncConfig,
  useUpdateNoteSyncConfig,
  useNoteSyncStatus,
  useNoteCategories,
  useSaveNoteCategories,
  useNoteSyncRecentWrites,
  useRunNoteSyncTick,
} from '../hooks/useNoteRequests';
import { formatDateTimeEastern, formatEventDateOnly, timeAgo } from '../utils/dates';
import { NoteDiff } from './NoteRequestBits';
import { tagOptionsProblem } from '../utils/noteTags';

// Admin → Note Sync tab: master switch, sweeper health, queue counts, the
// config knobs (auto-apply / separator / expiry / writes per tick / buyer tag choices), the
// category editor (label + public phrase per note type) and the last writes.
// Same draft/dirty/diff-save shape as AiAdminTab so only changed keys are PUT.

const SWEEPER_LABELS = {
  disabled: 'Off',
  manual: 'Pilot — matching only',
  idle: 'Idle',
  working: 'Working',
  paused_auth: 'Paused — Skybox API token rejected (check SKYBOX_* env)',
  stopped: 'Stopped',
};

const KEY_RE = /^[a-z0-9_]{2,40}$/;

function NoteSyncAdminTab({ onMessage }) {
  const { data, isLoading, error } = useNoteSyncConfig();
  const { data: status } = useNoteSyncStatus({ refetchInterval: 30000 });
  const { data: catData } = useNoteCategories(true);
  const { data: writesData } = useNoteSyncRecentWrites();
  const updateConfig = useUpdateNoteSyncConfig();
  const saveCategories = useSaveNoteCategories();
  const runTick = useRunNoteSyncTick();

  // ---- config draft ----
  const [draft, setDraft] = useState(null);
  useEffect(() => {
    if (data?.config && !draft) setDraft(data.config);
  }, [data, draft]);

  // ---- categories draft ----
  const [cats, setCats] = useState(null);
  useEffect(() => {
    if (catData?.categories && !cats) setCats(catData.categories.map(c => ({ ...c })));
  }, [catData, cats]);

  const serverCats = catData?.categories || [];
  const catsDirty = useMemo(
    () => cats != null && JSON.stringify(cats) !== JSON.stringify(serverCats.map(c => ({ ...c }))),
    [cats, serverCats]
  );

  if (isLoading || !draft) return <div className="loading">Loading Note Sync settings…</div>;
  if (error) return <div className="message message-error">{error.response?.data?.error || 'Failed to load Note Sync settings'}</div>;

  const cfg = data.config;
  const dirty = JSON.stringify(draft) !== JSON.stringify(cfg);
  const set = (key, value) => setDraft(d => ({ ...d, [key]: value }));

  const save = () => {
    const changes = {};
    for (const k of Object.keys(draft)) if (draft[k] !== cfg[k]) changes[k] = draft[k];
    if (!Object.keys(changes).length) return;
    updateConfig.mutate(changes, {
      onSuccess: (res) => { setDraft(res.data.config); onMessage?.('Note Sync settings saved'); },
      onError: (err) => onMessage?.(err.response?.data?.error || 'Failed to save', true),
    });
  };

  const toggleEnabled = () => {
    const next = !cfg.enabled;
    if (next && !window.confirm('Turn Note Sync on? Matched requests will be written to Skybox public notes' + (cfg.auto_apply ? ' automatically by the sweeper.' : ' when a pricer presses Apply.'))) return;
    updateConfig.mutate({ enabled: next }, {
      onSuccess: (res) => { setDraft(res.data.config); onMessage?.(next ? 'Note Sync enabled' : 'Note Sync disabled — no further writes'); },
      onError: (err) => onMessage?.(err.response?.data?.error || 'Failed to update', true),
    });
  };

  const sweeper = status?.sweeper || {};
  const counts = status?.counts || {};
  const cache = status?.cache || {};

  // ---- categories editor helpers ----
  const setCat = (idx, patch) => setCats(list => list.map((c, i) => (i === idx ? { ...c, ...patch } : c)));
  const addCat = () => setCats(list => [
    ...(list || []),
    { key: '', label: '', public_text: '', internal_text: '', requires_review: 0, sort_order: ((list || []).reduce((m, c) => Math.max(m, Number(c.sort_order) || 0), 0) + 10), is_active: 1, _new: true },
  ]);
  const removeNewCat = (idx) => setCats(list => list.filter((_, i) => i !== idx));

  const catProblems = (cats || []).map(c => {
    if (!KEY_RE.test(String(c.key || ''))) return 'key must be 2–40 chars of a-z, 0-9, _';
    if (!String(c.label || '').trim()) return 'label is required';
    if (c.key !== 'other' && !String(c.public_text || '').trim()) return 'public text is required (this is what gets written to Skybox)';
    return null;
  });
  const dupKeys = (() => {
    const seen = new Set(); const dup = new Set();
    for (const c of cats || []) { if (seen.has(c.key)) dup.add(c.key); seen.add(c.key); }
    return dup;
  })();
  const catsValid = catProblems.every(p => !p) && dupKeys.size === 0;

  const doSaveCategories = () => {
    if (!catsValid) { onMessage?.('Fix the highlighted category rows first', true); return; }
    const payload = (cats || []).map(c => ({
      key: String(c.key).trim(),
      label: String(c.label).trim(),
      public_text: String(c.public_text || '').trim(),
      internal_text: String(c.internal_text || '').trim(),
      requires_review: c.requires_review ? 1 : 0,
      sort_order: Number(c.sort_order) || 0,
      is_active: c.is_active ? 1 : 0,
    }));
    saveCategories.mutate(payload, {
      onSuccess: (res) => { setCats((res.data.categories || []).map(c => ({ ...c }))); onMessage?.('Categories saved'); },
      onError: (err) => onMessage?.(err.response?.data?.error || 'Failed to save categories', true),
    });
  };

  const sep = String(draft.separator ?? '');
  const sepProblem = sep.length < 1 || sep.length > 5 ? 'separator must be 1–5 characters' : null;
  const expiryProblem = !(Number(draft.waiting_expiry_days) >= 1) ? 'must be at least 1' : null;
  const writesProblem = !(Number(draft.max_writes_per_tick) >= 1 && Number(draft.max_writes_per_tick) <= 20) ? 'must be 1–20' : null;
  const tagOptsProblem = tagOptionsProblem(draft.tag_options);
  const configValid = !sepProblem && !expiryProblem && !writesProblem && !tagOptsProblem;

  const writes = writesData?.writes || [];

  return (
    <div className="card">
      <div className="card-header">
        <h2>Note Sync</h2>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span className={`ai-status-pill ${cfg.enabled ? 'on' : 'off'}`}>{cfg.enabled ? 'ON' : 'OFF'}</span>
          <button className={cfg.enabled ? 'btn-secondary' : 'btn-primary'} onClick={toggleEnabled} disabled={updateConfig.isPending}>
            {cfg.enabled ? 'Turn off' : 'Turn on'}
          </button>
        </div>
      </div>

      <div className="ai-admin-note" style={{ marginBottom: 12 }}>
        Buyers submit an order number + note type on <b>/orders</b>; the sweeper finds the listing in the inventory cache and
        {cfg.auto_apply ? ' writes the phrase to Skybox public notes itself (high-confidence matches only).' : ' waits for a pricer to press Apply (pilot mode).'}
        {' '}Off = nothing is written; requests are still accepted and matched.
      </div>

      {sweeper.status === 'paused_auth' && (
        <div className="message message-error" style={{ marginBottom: 12 }}>
          Skybox rejected the API token — public-note writes are paused{sweeper.pausedSince ? ` since ${formatDateTimeEastern(sweeper.pausedSince)}` : ''}. Fix SKYBOX_* in .env and restart, or press "Run one tick now" to re-probe.
        </div>
      )}

      <div className="ai-admin-grid">
        <div className="ai-admin-card">
          <h4>Sweeper</h4>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{SWEEPER_LABELS[sweeper.status] || sweeper.status || '—'}</div>
          <div className="ai-admin-note">
            last tick {sweeper.lastTickAt ? formatDateTimeEastern(sweeper.lastTickAt) : '—'}<br />
            last match pass {sweeper.lastMatchPassAt ? formatDateTimeEastern(sweeper.lastMatchPassAt) : '—'}<br />
            inventory synced {cache.lastDeltaAt ? formatDateTimeEastern(cache.lastDeltaAt) : '—'} (every 15 s)<br />
            last apply {sweeper.lastApplyAt ? formatDateTimeEastern(sweeper.lastApplyAt) : '—'}
            {sweeper.sinceBoot ? <><br />since boot: {sweeper.sinceBoot.matched ?? 0} matched · {sweeper.sinceBoot.applied ?? 0} applied · {sweeper.sinceBoot.verified ?? 0} verified · {sweeper.sinceBoot.skipped ?? 0} skipped · {sweeper.sinceBoot.failed ?? 0} failed · {sweeper.sinceBoot.expired ?? 0} expired</> : null}
            {sweeper.lastError ? <><br /><span className="nr-result-bad">last error: {sweeper.lastError}</span></> : null}
          </div>
          <button
            className="btn-secondary btn-sm"
            style={{ marginTop: 6 }}
            onClick={() => runTick.mutate(undefined, {
              onSuccess: (res) => onMessage?.(`Tick complete — sweeper ${SWEEPER_LABELS[res.data?.status?.status] || res.data?.status?.status || 'ran'}`),
              onError: (err) => onMessage?.(err.response?.data?.error || 'Tick failed', true),
            })}
            disabled={runTick.isPending}
          >
            {runTick.isPending ? 'Running…' : 'Run one tick now'}
          </button>
        </div>
        <div className="ai-admin-card">
          <h4>Queue</h4>
          <div className="big">{(counts.needsReview ?? 0) + (counts.matched ?? 0)}</div>
          <div className="ai-admin-note">
            {counts.waiting ?? 0} waiting · {counts.matched ?? 0} matched · {counts.needsReview ?? 0} need review · {counts.failedOpen ?? 0} failed writes open
          </div>
        </div>
        <div className="ai-admin-card">
          <h4>Today</h4>
          <div className="big">{counts.appliedToday ?? 0}</div>
          <div className="ai-admin-note">notes written · {counts.verifiedToday ?? 0} already present (verified)</div>
        </div>
        <div className="ai-admin-card">
          <h4>Inventory cache</h4>
          <div style={{ fontWeight: 600, fontSize: 14 }}>
            {cache.loaded ? `Loaded · synced ${cache.syncedAgoSeconds ?? 0}s ago · full refresh ${cache.ageSeconds ?? 0}s ago` : 'Not loaded yet'}
            {cache.refreshing ? ' · refreshing' : ''}
            {cache.delta?.lastErrorKind ? ` · delta sync failing (${cache.delta.lastErrorKind})` : ''}
          </div>
          <div className="ai-admin-note">Matching runs against this cache (0 Skybox calls). Writes pause while it refreshes.</div>
        </div>
        <div className="ai-admin-card">
          <h4>Discord</h4>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{status?.discord?.configured ? 'Configured' : 'Not configured'}</div>
          <div className="ai-admin-note">Set NOTE_SYNC_DISCORD_WEBHOOK_URL to get pings for new review items and auth pauses.</div>
        </div>
      </div>

      <h3 style={{ marginTop: 8 }}>Settings</h3>
      <div className="ai-admin-row">
        <label>Auto-apply</label>
        <input type="checkbox" checked={!!draft.auto_apply} onChange={e => set('auto_apply', e.target.checked)} />
        <span className="ai-admin-note">Sweeper writes public notes itself for high-confidence matches. Off = pilot: pricers press Apply and see the diff.</span>
      </div>
      <div className="ai-admin-row">
        <label>Separator</label>
        <input type="text" value={sep} onChange={e => set('separator', e.target.value)} style={{ width: 70, fontFamily: 'monospace' }} maxLength={5} />
        <span className="ai-admin-note">
          Preview: <code className="nr-sep-preview">{`Ages 21+${sep}Limited View`}</code>
          {sepProblem ? <span className="nr-result-bad"> · {sepProblem}</span> : ` · ${sep.length} char${sep.length === 1 ? '' : 's'}${/^\s+$/.test(sep) ? ' (whitespace)' : ''}`}
        </span>
      </div>
      <div className="ai-admin-row">
        <label>Waiting expiry (days)</label>
        <input type="number" min="1" step="1" value={draft.waiting_expiry_days} onChange={e => set('waiting_expiry_days', Number(e.target.value))} style={{ width: 90 }} />
        <span className="ai-admin-note">A request with no listing after this many days expires; the buyer is told to check the number and resubmit.{expiryProblem ? <span className="nr-result-bad"> · {expiryProblem}</span> : null}</span>
      </div>
      <div className="ai-admin-row">
        <label>Max writes per tick</label>
        <input type="number" min="1" max="20" step="1" value={draft.max_writes_per_tick} onChange={e => set('max_writes_per_tick', Number(e.target.value))} style={{ width: 90 }} />
        <span className="ai-admin-note">Each write is one Skybox GET + PUT with a 1.1 s gap; the tick runs every 60 s.{writesProblem ? <span className="nr-result-bad"> · {writesProblem}</span> : null}</span>
      </div>
      <div className="ai-admin-row">
        <label htmlFor="ns-tag-options">Tag choices for buyers</label>
        <input id="ns-tag-options" type="text" className="nr-mono" value={draft.tag_options ?? ''} onChange={e => set('tag_options', e.target.value)} placeholder="e.g. 1test 2test" style={{ width: 260 }} />
        <span className="ai-admin-note">Chips on the /orders form that add a Skybox tag (staff only). Separate with spaces; no spaces inside a tag. Empty hides the chips.{tagOptsProblem ? <span className="nr-result-bad"> · {tagOptsProblem}</span> : null}</span>
      </div>
      <div className="ai-admin-row" style={{ marginTop: 10 }}>
        <button className="btn-primary" onClick={save} disabled={!dirty || !configValid || updateConfig.isPending}>{updateConfig.isPending ? 'Saving…' : 'Save settings'}</button>
        {dirty && <button className="btn-secondary" onClick={() => setDraft(cfg)}>Discard</button>}
      </div>

      <h3 style={{ marginTop: 20 }}>Note types (categories)</h3>
      <div className="ai-admin-note" style={{ marginBottom: 6 }}>
        <b>Public text</b> is the exact phrase appended to the listing's public notes — buyers on marketplaces see it. <b>Internal text</b> (optional) is appended to the listing's internal Skybox notes at the same time — staff only, e.g. "smp wallet" for ADA. <b>Requires review</b> sends every request of that type to the pricer queue instead of auto-apply. Inactive types are hidden from the buyer form (existing requests keep working).
        {' '}Edits affect future applies only; existing listings keep the text already written.
      </div>
      <div className="table-container">
        <table className="nr-cat-table">
          <thead>
            <tr><th>Key</th><th>Label</th><th>Public text</th><th>Internal text</th><th>Requires review</th><th>Active</th><th>Sort</th><th></th></tr>
          </thead>
          <tbody>
            {(cats || []).map((c, idx) => {
              const problem = catProblems[idx] || (dupKeys.has(c.key) ? 'duplicate key' : null);
              return (
                <tr key={c._new ? `new-${idx}` : c.key} className={problem ? 'nr-row-problem' : ''}>
                  <td>
                    {c._new ? (
                      <input type="text" className="form-input form-input-sm nr-mono" value={c.key} onChange={e => setCat(idx, { key: e.target.value.toLowerCase() })} placeholder="e.g. aisle" style={{ width: 130 }} />
                    ) : (
                      <span className="nr-mono">{c.key}</span>
                    )}
                    {problem ? <div className="nr-result-bad" style={{ fontSize: 11 }}>{problem}</div> : null}
                  </td>
                  <td><input type="text" className="form-input form-input-sm" value={c.label || ''} onChange={e => setCat(idx, { label: e.target.value })} style={{ width: 160 }} /></td>
                  <td>
                    <input type="text" className="form-input form-input-sm" value={c.public_text || ''} onChange={e => setCat(idx, { public_text: e.target.value })} placeholder={c.key === 'other' ? '(buyer types the text)' : 'e.g. Limited View'} style={{ width: 220 }} disabled={c.key === 'other'} />
                  </td>
                  <td>
                    <input type="text" className="form-input form-input-sm" value={c.internal_text || ''} onChange={e => setCat(idx, { internal_text: e.target.value })} placeholder="(none)" title="Appended to the listing's internal Skybox notes (staff only)" style={{ width: 160 }} />
                  </td>
                  <td style={{ textAlign: 'center' }}><input type="checkbox" checked={!!c.requires_review} onChange={e => setCat(idx, { requires_review: e.target.checked ? 1 : 0 })} /></td>
                  <td style={{ textAlign: 'center' }}><input type="checkbox" checked={!!c.is_active} onChange={e => setCat(idx, { is_active: e.target.checked ? 1 : 0 })} /></td>
                  <td><input type="number" className="form-input form-input-sm" value={c.sort_order ?? 0} onChange={e => setCat(idx, { sort_order: Number(e.target.value) })} style={{ width: 70 }} /></td>
                  <td>{c._new ? <button className="btn btn-link btn-sm" onClick={() => removeNewCat(idx)}>remove</button> : null}</td>
                </tr>
              );
            })}
            {(!cats || cats.length === 0) && (
              <tr><td colSpan="7" className="nr-muted" style={{ textAlign: 'center' }}>No categories yet</td></tr>
            )}
          </tbody>
        </table>
      </div>
      <div className="ai-admin-row" style={{ marginTop: 8 }}>
        <button className="btn-secondary btn-sm" onClick={addCat}>+ Add category</button>
        <span style={{ flex: 1 }} />
        {catsDirty && <button className="btn-secondary" onClick={() => setCats(serverCats.map(c => ({ ...c })))}>Discard</button>}
        <button className="btn-primary" onClick={doSaveCategories} disabled={!catsDirty || !catsValid || saveCategories.isPending}>
          {saveCategories.isPending ? 'Saving…' : 'Save categories'}
        </button>
      </div>

      <h3 style={{ marginTop: 20 }}>Recent writes</h3>
      {writes.length === 0 ? (
        <div className="ai-admin-note">No public-note writes yet.</div>
      ) : (
        <div className="table-container">
          <table className="nr-writes-table">
            <thead>
              <tr><th>Time</th><th>Order</th><th>Listing</th><th>Before → after</th><th>By</th></tr>
            </thead>
            <tbody>
              {writes.map((w, i) => (
                <tr key={`${w.requestId}-${w.inventoryId}-${i}`}>
                  <td style={{ whiteSpace: 'nowrap' }} title={formatDateTimeEastern(w.appliedAt)}>{timeAgo(w.appliedAt)}</td>
                  <td className="nr-mono">{w.orderNumber}{w.requestId ? <span className="nr-muted"> #{w.requestId}</span> : null}</td>
                  <td style={{ maxWidth: 260 }}>
                    <div>{w.eventName}</div>
                    <div className="nr-muted" style={{ fontSize: 11 }}>{formatEventDateOnly(w.eventDate)} · Sec {w.section} Row {w.row} · #{w.inventoryId}</div>
                  </td>
                  <td className="nr-notes-cell"><NoteDiff before={w.beforeText} after={w.afterText} /></td>
                  <td style={{ whiteSpace: 'nowrap' }}>{w.appliedSource === 'auto' ? 'auto (sweeper)' : (w.appliedByName || 'manual')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default NoteSyncAdminTab;
